// ---------------------------------------------------------------------------
// overlayHost.js — a single, always-mounted host for the feature OVERLAYS
// (mask-refine dialog, export dialog, shortcuts help). Owned by the Group G / H
// agent.
//
// Feature modules registered via `registerFeatures(editor)` run imperatively —
// they are NOT in React's tree, so they cannot render a portal on their own. The
// foundation exposes exactly two React seams: `addToolButton` and
// `addPanelSection`. This host uses ONE `addPanelSection` (registered lazily the
// first time any overlay is added) whose component mounts every overlay through
// `ReactDOM.createPortal(..., document.body)`.
//
// Why a panel section and not a private ReactDOM root: a private root would leak
// on editor unmount (there is no feature-teardown seam). A panel section unmounts
// with the editor tree, so React tears the portals down for us — no leaked DOM,
// no leaked listeners.
//
// The host renders NOTHING visible in the left column: its only in-place child is
// a zero-size marker span, and `featureOverlays.css` hides the wrapper the
// foundation draws around it (`.de-panel-section:has(> .de-overlay-host-marker)`).
//
// Each overlay component is a self-contained function component (its own hooks /
// state) and re-renders independently of the foundation. It typically publishes
// an imperative opener onto the shared `editor.overlays` namespace (created here)
// so tool buttons and keyboard commands can open it.
// ---------------------------------------------------------------------------

import React from 'react';
import { createPortal } from 'react-dom';
import './featureOverlays.css';

function OverlayHost({ editor }) {
  const overlays = editor._deOverlays || [];
  return (
    <>
      <span className="de-overlay-host-marker" aria-hidden="true" />
      {overlays.map(({ key, Comp }) => (
        <React.Fragment key={key}>
          {createPortal(<Comp editor={editor} />, document.body)}
        </React.Fragment>
      ))}
    </>
  );
}

/**
 * Register an overlay component to be mounted (via a portal) for the lifetime of
 * the editor. `key` must be unique per editor. `Comp` receives `{ editor }`.
 *
 * All overlays share ONE host panel section, registered on the first call. Every
 * overlay is added synchronously from `registerFeatures`, before the first render
 * settles, so the host renders the complete set.
 */
export function registerOverlay(editor, key, Comp) {
  if (!editor.overlays) editor.overlays = {};
  if (!editor._deOverlays) {
    editor._deOverlays = [];
    editor.addPanelSection({
      id: 'de-overlay-host',
      order: 100000, // last — it renders nothing visible anyway
      render: (ed) => <OverlayHost editor={ed} />,
    });
  }
  if (editor._deOverlays.some((o) => o.key === key)) return;
  editor._deOverlays.push({ key, Comp });
}
